import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getCharacterInventory,
  getDailies,
  getGeneralGameState,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { SettingsPanelController } from '@/game/GameComponents/PhaserGame/Scenes/Settings/SettingsPanelController';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import {
  isPhaserDevMode,
  waitForSeconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import DailiesCompleteNotification from '@/game/prefabs/WorldUI/DailiesCompleteNotification';
import HUD from '@/game/prefabs/WorldUI/HUD/HUD';

export default class HUDController {
  constructor(private hud: HUD) {
    hud.curtain.setVisible(true);

    // NPC guidance setup
    // Default values.
    this.setRemainingNpcsText(0, 0);

    // Desktop buttons.
    hud.toggleGuidesButton.setSubmissionCallback(
      this.handleToggleGuidesButtonSubmitted,
    );
    hud.inventoryButton.setSubmissionCallback(
      this.handleInventoryButtonSubmitted,
    );
    hud.masteriesButton.setSubmissionCallback(
      this.handleMasteriesButtonSubmitted,
    );
    hud.shopButton.setSubmissionCallback(this.handleShopButtonSubmitted);
    hud.helpButton.setSubmissionCallback(this.handleHelpButtonSubmitted);
    hud.settingsButton.setSubmissionCallback(
      this.handleSettingsButtonSubmitted,
    );
    hud.logoutButton.setSubmissionCallback(this.handleLogoutButtonSubmitted);
    hud.dailiesButton.setSubmissionCallback(this.handleDailiesButtonSubmitted);

    // Mobile buttons.
    hud.expandCollapseButton_m.setSubmissionCallback(() => {
      hud.collapsableElements.setVisible(!hud.collapsableElements.visible);
    });
    hud.toggleGuidesButton_m.setSubmissionCallback(
      this.handleToggleGuidesButtonSubmitted,
    );
    hud.inventoryButton_m.setSubmissionCallback(
      this.handleInventoryButtonSubmitted,
    );
    hud.masteriesButton_m.setSubmissionCallback(
      this.handleMasteriesButtonSubmitted,
    );
    hud.shopButton_m.setSubmissionCallback(this.handleShopButtonSubmitted);
    hud.helpButton_m.setSubmissionCallback(this.handleHelpButtonSubmitted);
    hud.settingsButton_m.setSubmissionCallback(
      this.handleSettingsButtonSubmitted,
    );
    hud.logoutButton_m.setSubmissionCallback(this.handleLogoutButtonSubmitted);
    hud.dailiesButton_m.setSubmissionCallback(
      this.handleDailiesButtonSubmitted,
    );

    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.COIN_VALUE_CHANGED,
      this.handleCoinsChanged,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.DUEL_STARTED,
      this.handleDuelStarted,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.DUEL_ENDED,
      this.handleDuelEnded,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.PLAYER_QUIT_DUEL,
      this.handleDuelEnded,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.FINISHED_DISPLAYING_DAILY_REWARD,
      this.handleFinishedDisplayingDaily,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.FINISHED_NPC_ASSET_PRELOAD,
      this.handleFinishedLoadingNPCs,
      this,
    );
    EventsManager.registerEventOnce(
      hud.scene,
      GAME_EVENTS.TILEMAP_LEVEL_INITIALIZED,
      this.refresh,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.COSMETIC_SHOP_OPENED,
      this.handleShopOpened,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.COSMETIC_SHOP_CLOSED,
      this.handleShopClosed,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.INVENTORY_OPENED,
      this.handleInventoryOpened,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.INVENTORY_CLOSED,
      this.handleInventoryClosed,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.MINIGAME_STARTED,
      this.handleMinigameStarted,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.MINIGAME_ENDED,
      this.handleMinigameEnded,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.ENTERED_NEW_BIOME,
      this.handleEnteredNewBiome,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.MENTOR_INTRODUCTION_PANEL_CLOSED,
      this.handleMentorIntroPanelClosed,
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.PLAYER_VIEWED_LEVEL_LESSON,
      () => this.hud.setVisible(false),
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.PLAYER_ENDED_LEVEL_LESSON,
      () => this.hud.setVisible(true),
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.DAILY_PANEL_OPENED,
      () => this.hud.setVisible(false),
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.DAILY_PANEL_CLOSED,
      () => this.hud.setVisible(true),
      this,
    );
    EventsManager.registerEventOn(
      hud.scene,
      GAME_EVENTS.PLAYER_CUSTOMIZATION_ASSETS_LOADED,
      this.handleFinishedLoadingPlayerCosmetics,
      this,
    );
    hud.scene.events.once('create', () => this.handleSceneReady());
    hud.scene.events.once('destroy', this.handleSceneDestroy, this);
    hud.scene.events.once('shutdown', this.handleSceneDestroy, this);
    this.levelMap = null;
    this.backgroundMusic = null;

    this.initializeResponsiveness();

    // Helpful Dev stuff for debugging
    const student = getStudent();
    hud.completeAllNodesButton.setVisible(isPhaserDevMode(student));
    hud.buyAllItemsButton.setVisible(isPhaserDevMode(student));
    hud.completeAllNodesButton.setSubmissionCallback(() => {
      EventBus.emit(GAME_EVENTS.PLAYER_REQUESTED_COMPLETE_ALL_NODES);
    });
    hud.buyAllItemsButton.setSubmissionCallback(() => {
      EventBus.emit(GAME_EVENTS.BUY_ALL_ITEMS);
    });
  }

  levelMap: Phaser.Scene | null;
  backgroundMusic: Phaser.Sound.BaseSound | null;
  anchor: any;
  npcAssetsLoaded: boolean = false;
  playerCosmeticsLoaded: boolean = false;

  getResponsiveElements(key: keyof HUD) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromContainer(
      this.hud,
      key,
    );
  }

  preload() {}

  init(data: any) {
    this.levelMap = data.levelMap;
    this.levelMap?.events.on('resume', this.refresh, this);
  }

  initializeResponsiveness() {
    const { hud } = this;
    // Breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: hud.scene,
      desktopView: hud.rightPanel,
      mobileView: hud.rightPanel_m,
    });

    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: hud.scene,
      desktopView: hud.leftPanel,
      mobileView: hud.leftPanel_m,
    });

    // Desktop.
    this.anchor = hud.scene.plugins.get('rexanchorplugin');
    this.anchor.add(hud.rightPanel, {
      right: '100%',
    });
    this.anchor.add(hud.curtain, {
      width: '100%',
      height: '100%',
    });
    ResponsivenessManager.MakeElementsScaleDownWithGameSize(
      [hud.rightPanel, hud.leftPanel],
      { scaleModifier: 2 },
    );

    // Mobile.
    this.anchor.add(hud.rightPanel_m, {
      right: '100%',
    });

    ResponsivenessManager.MakeElementsScaleDownWithGameSize(
      [hud.rightPanel_m],
      { scaleModifier: 1 },
    );
  }

  awake() {
    this.handleCoinsChanged(getCharacterInventory().coins);
  }

  setRemainingNpcsText(numCompletedNodes: number, numNodesInLevel: number) {
    const newText = `${numCompletedNodes}/${numNodesInLevel}`;
    this.getResponsiveElements('remainingNPCsText').forEach(
      (text: Phaser.GameObjects.BitmapText) => {
        text.setText(newText);
      },
    );
  }

  doHeartFeedback(numCompletedNodes: number, numNodesInLevel: number) {
    const { hud } = this;

    this.getResponsiveElements('heartIcon').forEach(
      (icon: Phaser.GameObjects.BitmapText) => {
        hud.scene.tweens.add({
          targets: icon,
          scale: 2.1,
          duration: 400,
          ease: 'Back.easeInOut',
          delay: 400,
          onComplete: () => {
            hud.scene.tweens.add({
              targets: icon,
              scale: 2.4,
              duration: 400,
              ease: 'Expo.easeOut',
              onComplete: () => {
                hud.scene.tweens.add({
                  targets: icon,
                  scale: 2,
                  duration: 400,
                  ease: 'Back.easeInOut',
                });
              },
            });

            hud.scene.tweens.add({
              targets: icon,
              x: icon.x + 5,
              duration: 50,
              yoyo: true,
              repeat: 5,
              ease: 'Sine.easeInOut',
            });
          },
        });
      },
    );

    this.getResponsiveElements('remainingNPCsText').forEach(
      (text: Phaser.GameObjects.BitmapText) => {
        hud.scene.tweens.add({
          targets: text,
          scale: 1.2,
          yoyo: true,
          duration: 750,
          ease: 'Sine.easeInOut',
          delay: 400,
        });
      },
    );

    waitForSeconds(hud.scene.time, 1.35).then(() => {
      this.setRemainingNpcsText(numCompletedNodes, numNodesInLevel);
    });
  }

  showDailiesCompleteNotification() {
    const { hud } = this;
    this.getResponsiveElements('notificationContainer').forEach(
      (notificationContainer: Phaser.GameObjects.Container) => {
        const notif = new DailiesCompleteNotification(hud.scene, 0, 0);
        hud.scene.add.existing(notif);
        notificationContainer.add(notif);
      },
    );
  }

  async handleSceneReady() {
    console.log('hud controller handle scene ready');
    const { hud } = this;
    await waitUntil(
      hud.scene.time,
      () => this.npcAssetsLoaded && this.playerCosmeticsLoaded,
    );
    // Reveal the curtain with a fade out effect.
    hud.scene.add.tween({
      targets: hud.curtain,
      alpha: 0,
      duration: 500,
      onComplete: () => {
        hud.curtain.setVisible(false);
      },
    });
  }

  handleCoinsChanged(newCoins: number) {
    this.getResponsiveElements('coinsLabel').forEach(
      (label: Phaser.GameObjects.BitmapText) => {
        label.setText(newCoins.toString());
      },
    );
  }

  handleDuelStarted() {
    this.hud.setVisible(false);
  }

  handleDuelEnded() {
    this.hud.setVisible(true);
  }

  handleShopOpened() {
    this.hud.setVisible(false);
  }

  handleShopClosed() {
    const { hud } = this;
    // The HUD should NOT be in charge of resuming the scene!
    hud.scene.scene.resume();
    hud.setVisible(true);
  }

  handleInventoryOpened() {
    this.hud.setVisible(false);
  }

  handleInventoryClosed() {
    this.hud.setVisible(true);
  }

  handleMinigameStarted() {
    this.hud.setVisible(false);
  }

  handleMinigameEnded() {
    this.hud.setVisible(true);
  }

  handleFinishedDisplayingDaily() {
    const { hud } = this;
    const dailies = getDailies();
    let duelDailyComplete =
      (dailies.duel.shouldCompleteDuel && dailies.duel.hasCompletedDuel) ||
      !dailies.duel.shouldCompleteDuel;
    let minigamesDailyComplete =
      dailies.minigames.numCompletedMinigames >=
      dailies.minigames.targetNumMinigames;
    if (duelDailyComplete && minigamesDailyComplete) {
      this.showDailiesCompleteNotification();
      const notif = new DailiesCompleteNotification(hud.scene, 0, 0);
      hud.scene.add.existing(notif);
      hud.notificationContainer.add(notif);
    }
  }

  handleInventoryButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.INVENTORY_OPENED);
  }

  handleMasteriesButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.MASTERIES_PANEL_OPENED);
  }

  handleShopButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.COSMETIC_SHOP_OPENED, this.hud);
  }

  handleHelpButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.PLAYER_REQUESTED_HUD_WALKTHROUGH);
  }

  handleSettingsButtonSubmitted() {
    SettingsPanelController.config = {};
    EventBus.emit(GAME_EVENTS.SETTINGS_PANEL_OPENED);
  }

  handleDailiesButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.PLAYER_REQUESTED_OPEN_DAILIES_PANEL);
  }

  handleLogoutButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.PLAYER_REQUESTED_QUIT_MAIN_LEVEL);
  }

  handleToggleGuidesButtonSubmitted() {
    EventBus.emit(GAME_EVENTS.PLAYER_REQUESTED_NPC_GUIDANCE);
  }

  handleEnteredNewBiome() {
    this.hud.setVisible(false);
  }

  handleMentorIntroPanelClosed() {
    this.hud.setVisible(true);
  }

  handleFinishedLoadingPlayerCosmetics() {
    this.playerCosmeticsLoaded = true;
  }

  handleFinishedLoadingNPCs() {
    this.npcAssetsLoaded = true;
  }

  refresh() {
    const { enabledGames, gamesProgress, completedPlaythroughs } =
      GameInstance.instance.getActiveLevelProgress();
    const numNodesInLevel = enabledGames.length;
    const numCompletedNodes = Object.values(gamesProgress).filter(
      (game) => game.timesPlayed > completedPlaythroughs,
    ).length;
    this.setRemainingNpcsText(numCompletedNodes, numNodesInLevel);

    // Heart feedback
    if (getGeneralGameState().lastCompletedGame) {
      this.setRemainingNpcsText(numCompletedNodes - 1, numNodesInLevel);

      this.doHeartFeedback(numCompletedNodes, numNodesInLevel);

      EventBus.emit(GAME_EVENTS.FINISHED_NODE_COMPLETE_FEEDBACK);
    }
  }

  handleSceneDestroy() {
    this.levelMap?.events.off('resume', this.refresh, this);
  }
}
